// Thin fetch wrappers around /api/conversations.
//
// Date filters can be ISO strings or preset tokens. Preset → ISO conversion
// happens here so the API never sees "last-30d".

import type {
  ConversationDetail,
  ConversationFilters,
  ConversationListResponse,
} from "./types";

const PRESET_TO_DAYS: Record<string, number> = {
  "last-7d": 7,
  "last-30d": 30,
  "last-90d": 90,
};

function resolveTimestamp(value: string | null): string | null {
  if (!value) return null;
  const days = PRESET_TO_DAYS[value];
  if (days !== undefined) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
  }
  return value;
}

export function buildQueryString(filters: ConversationFilters, limit = 50, offset = 0): string {
  const params = new URLSearchParams();
  if (filters.participants.length > 0) {
    params.set("participants", filters.participants.join(","));
    if (filters.op !== "and") params.set("op", filters.op);
  }
  if (filters.kinds.length > 0) {
    params.set("kind", filters.kinds.join(","));
  }
  const since = resolveTimestamp(filters.since);
  if (since) params.set("since", since);
  const until = resolveTimestamp(filters.until);
  if (until) params.set("until", until);
  if (filters.visibility.length > 0) {
    params.set("visibility", filters.visibility.join(","));
  }
  if (filters.q) params.set("q", filters.q);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return params.toString();
}

async function fetchJson<T>(path: string): Promise<T> {
  const resp = await fetch(path, { credentials: "include" });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`GET ${path}: ${resp.status} ${resp.statusText} ${text}`);
  }
  return (await resp.json()) as T;
}

export async function listConversations(
  filters: ConversationFilters,
  limit = 50,
  offset = 0,
): Promise<ConversationListResponse> {
  const qs = buildQueryString(filters, limit, offset);
  return fetchJson<ConversationListResponse>(`/api/conversations?${qs}`);
}

export async function getConversation(
  id: string,
  turnsCursor?: string,
  turnsLimit = 200,
): Promise<ConversationDetail> {
  const params = new URLSearchParams();
  if (turnsCursor) params.set("turns_cursor", turnsCursor);
  params.set("turns_limit", String(turnsLimit));
  return fetchJson<ConversationDetail>(`/api/conversations/${id}?${params}`);
}
